import { useEffect, useRef } from "react";
import "@/App.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Header from "@/sections/Header";
import Hero from "@/sections/Hero";
import Marquee from "@/sections/Marquee";
import Differentials from "@/sections/Differentials";
import About from "@/sections/About";
import Products from "@/sections/Products";
import Testimonials from "@/sections/Testimonials";
import Contact from "@/sections/Contact";
import Footer from "@/sections/Footer";

gsap.registerPlugin(ScrollTrigger);

function App() {
  const rootRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ─── 1. Fade-up padrão (mais evidente: y maior, blur, scale leve) ───
      gsap.utils.toArray(".reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 80, filter: "blur(6px)", scale: 0.97 },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            scale: 1,
            duration: 1.3,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 2. Fade da esquerda ───────────────────────────────────────────
      gsap.utils.toArray(".reveal-left").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, x: -80, filter: "blur(4px)" },
          {
            opacity: 1,
            x: 0,
            filter: "blur(0px)",
            duration: 1.2,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 3. Fade da direita ───────────────────────────────────────────
      gsap.utils.toArray(".reveal-right").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, x: 80, filter: "blur(4px)" },
          {
            opacity: 1,
            x: 0,
            filter: "blur(0px)",
            duration: 1.2,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 4. Scale-up (para imagens e cards grandes) ───────────────────
      gsap.utils.toArray(".reveal-scale").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, scale: 0.88, filter: "blur(8px)" },
          {
            opacity: 1,
            scale: 1,
            filter: "blur(0px)",
            duration: 1.4,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 90%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 5. Clip vertical (para headings com "cortina") ───────────────
      gsap.utils.toArray(".reveal-clip").forEach((el) => {
        gsap.fromTo(
          el,
          { clipPath: "inset(0 0 100% 0)", opacity: 0, y: 30 },
          {
            clipPath: "inset(0 0 0% 0)",
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 6. Stagger de grupo (mais dramático: y 60, stagger 0.15) ────
      gsap.utils.toArray("[data-stagger-group]").forEach((group) => {
        const items = group.querySelectorAll("[data-stagger-item]");
        gsap.fromTo(
          items,
          { opacity: 0, y: 70, filter: "blur(4px)", scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            scale: 1,
            duration: 1.1,
            ease: "expo.out",
            stagger: 0.15,
            scrollTrigger: {
              trigger: group,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 7. Linha horizontal separadora (largura 0 → 100%) ───────────
      gsap.utils.toArray(".reveal-line").forEach((el) => {
        gsap.fromTo(
          el,
          { scaleX: 0, transformOrigin: "left center" },
          {
            scaleX: 1,
            duration: 1.4,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 90%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // ─── 8. Eyebrow labels (fade + slide leve) ────────────────────────
      gsap.utils.toArray(".reveal-eyebrow").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, x: -24, letterSpacing: "0.5em" },
          {
            opacity: 1,
            x: 0,
            letterSpacing: "0.28em",
            duration: 1,
            ease: "expo.out",
            scrollTrigger: {
              trigger: el,
              start: "top 90%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });

    }, rootRef);

    return () => ctx.revert();
  }, []);


  return (
    <div className="App font-body" ref={rootRef} data-testid="app-root">
      <Header />
      <main>
        <Hero />
        <Marquee />
        <Differentials />
        <About />
        <Products />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
